/**
 * Рамка и окно дат домена — витринная сторона.
 *
 * Решение владельца 20.10.2026 (`docs/plans/2026-10-20-domain-frame-period-design.md`):
 * кабинет домена работает только внутри своей рамки и своего окна дат. Сервер
 * держит то же самое (`src/cabinet/domain_limits.py`) — витрина лишь не даёт
 * человеку увести карту или дату туда, где он получит отказ.
 *
 * Пределы приезжают в бренде домена (`GET /cabinet/site` → `brand.frame`,
 * `brand.period`). Нет поля — нет ограничения, как и на сервере.
 */

/** Ползунок дат без ограничения домена: неделю назад, два дня вперёд. */
export const DAY_MIN = -7
export const DAY_MAX = 2

/** Рамка домена. Долгота НЕПРЕРЫВНА на восток: ДВ — 130…195 (= 165° з.д.). */
export interface DomainFrame {
  lat_min: number
  lat_max: number
  lon_min: number
  lon_max: number
}

/** Окно дат: сегодня − past_days … сегодня + future_days. */
export interface DomainPeriod {
  past_days: number
  future_days: number
  /** Есть ли прошлое вовсе. Без него снимается всё, что из прошлого строится. */
  history: boolean
}

export interface DomainLimits {
  frame: DomainFrame | null
  period: DomainPeriod | null
}

export const NO_LIMITS: DomainLimits = { frame: null, period: null }

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** Битая рамка — `null` («не ограничено»), а не падение: сервер всё равно держит своё. */
export function parseFrame(raw: unknown): DomainFrame | null {
  if (!raw || typeof raw !== 'object') return null
  const b = raw as Record<string, unknown>
  if (![b.lat_min, b.lat_max, b.lon_min, b.lon_max].every(num)) return null
  const f = b as unknown as DomainFrame
  if (!(f.lat_min < f.lat_max) || !(f.lon_min < f.lon_max)) return null
  return { lat_min: f.lat_min, lat_max: f.lat_max, lon_min: f.lon_min, lon_max: f.lon_max }
}

export function parsePeriod(raw: unknown): DomainPeriod | null {
  if (!raw || typeof raw !== 'object') return null
  const b = raw as Record<string, unknown>
  const past = b.past_days
  const future = b.future_days
  const whole = (v: unknown): v is number => num(v) && Number.isInteger(v) && v >= 0
  if (!whole(past) || !whole(future)) return null
  return {
    past_days: Math.min(past, -DAY_MIN),
    future_days: Math.min(future, DAY_MAX),
    history: past > 0,
  }
}

/** Диапазон ползунка в сутках от сегодня. */
export interface DayRange {
  min: number
  max: number
}

export function dayRange(period: DomainPeriod | null | undefined): DayRange {
  if (!period) return { min: DAY_MIN, max: DAY_MAX }
  // `|| 0`: без истории начало оси — ноль, а не −0 (Object.is их различает).
  return { min: -period.past_days || 0, max: period.future_days }
}

/** Есть ли у домена прошлое. Без окна — есть (прежние −7…+2). */
export function hasHistory(limits: DomainLimits): boolean {
  return limits.period == null || limits.period.history
}

const DAY_MS = 86_400_000
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function shift(today: string, days: number): string {
  const [y, m, d] = today.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d) + days * DAY_MS).toISOString().slice(0, 10)
}

/** Первая и последняя разрешённые даты; `null` — окно не задано. */
export function dateBounds(
  period: DomainPeriod | null | undefined,
  today: string,
): { min: string; max: string } | null {
  if (!period) return null
  return { min: shift(today, -period.past_days), max: shift(today, period.future_days) }
}

/** Дата, прижатая к окну домена. Строки ISO сравниваются как даты. */
export function clampDate(date: string, today: string, period: DomainPeriod | null | undefined): string {
  const b = dateBounds(period, today)
  if (!b || !DATE_RE.test(date)) return date
  if (date < b.min) return b.min
  if (date > b.max) return b.max
  return date
}

/** `maxBounds` MapLibre: [[запад, юг], [восток, север]]. Восток за 180° MapLibre
 *  понимает сам (рамка через шов), поэтому долгота НЕ сворачивается. */
export function frameBounds(
  frame: DomainFrame | null | undefined,
): [[number, number], [number, number]] | undefined {
  if (!frame) return undefined
  return [[frame.lon_min, frame.lat_min], [frame.lon_max, frame.lat_max]]
}

// ── регистр для императивного кода ────────────────────────────────────────
//
// Карты строятся синхронно в эффектах (`new maplibregl.Map({...})`), контекста
// React там нет. Пределы кладёт `LangProvider` тем же движением, что подложку и
// гамму линий, — до первого кадра, из ответа домена, запомненного `siteCache`.

let current: DomainLimits = NO_LIMITS

export function applyDomainLimits(next: DomainLimits): void {
  current = next
}

export function currentDomainLimits(): DomainLimits {
  return current
}

/** `maxBounds` для конструктора карты: карта не уводится за рамку домена. */
export function domainMaxBounds(): [[number, number], [number, number]] | undefined {
  return frameBounds(current.frame)
}
